import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  TextField,
  Box,
  ButtonGroup,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

// Styled Buttons
const ApproveButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  minWidth: 90,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
}));

const RejectButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  minWidth: 90,
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

const AdminPage = () => {
  const [resellers, setResellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchResellers = async () => {
    try {
      const res = await axios.get('https://reviewhub-lc.onrender.com/api/resellers/all');
      setResellers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`https://reviewhub-lc.onrender.com/api/resellers/${id}/status`, { status });

      alert(status === 1 ? '✅ Reseller approved' : '❌ Reseller rejected');
      fetchResellers();
    } catch (err) {
      console.error(err);
      alert('Something went wrong!');
    }
  };

  useEffect(() => {
    fetchResellers();
  }, []);

  const filteredResellers = resellers.filter((reseller) => {
    const searchMatch =
      reseller.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.resellerId?.toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch =
      statusFilter === 'all' ||
      (statusFilter === 'approved' && reseller.status === 1) ||
      (statusFilter === 'rejected' && reseller.status === 0);

    return searchMatch && statusMatch;
  });

  const renderStatusChip = (status) => {
    if (status === 1) return <Chip label="Approved" color="success" size="small" />;
    if (status === 0) return <Chip label="Rejected" color="error" size="small" />;
    return <Chip label="Pending" color="warning" size="small" />;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Reseller Approval Panel
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Search and Filter */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <TextField
          label="Search Reseller"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: '100%', maxWidth: 400 }}
          size="small"
        />

        <ButtonGroup variant="outlined" size="small">
          <Button
            variant={statusFilter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'approved' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('approved')}
          >
            Approved
          </Button>
          <Button
            variant={statusFilter === 'rejected' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('rejected')}
          >
            Rejected
          </Button>
        </ButtonGroup>
      </Box>

      {/* Reseller Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Profile</TableCell>
              <TableCell>Reseller ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredResellers.map((reseller) => (
              <TableRow key={reseller._id} hover>
                <TableCell>
                  <Avatar src={reseller.profileImageUrl} alt={reseller.name} />
                </TableCell>
                <TableCell>{reseller.resellerId}</TableCell>
                <TableCell>{reseller.name}</TableCell>
                <TableCell>{reseller.email}</TableCell>
                <TableCell>{reseller.phone}</TableCell>
                <TableCell>{renderStatusChip(reseller.status)}</TableCell>
                <TableCell>
                  <ApproveButton
                    size="small"
                    onClick={() => updateStatus(reseller._id, 1)}
                    disabled={reseller.status === 1}
                  >
                    {reseller.status === 1 ? 'Approved' : 'Approve'}
                  </ApproveButton>
                  <RejectButton
                    size="small"
                    onClick={() => updateStatus(reseller._id, 0)}
                    disabled={reseller.status === 0}
                  >
                    {reseller.status === 0 ? 'Rejected' : 'Reject'}
                  </RejectButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredResellers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No resellers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminPage;
